import { useEffect, useRef } from "react";

import { PANEL_TEARDOWN_EVENT, whenBootAllowed } from "./host";
import { reconnectBody } from "./reconnect-body";
import { wsDiag } from "./ws-diag";

/**
 * Release the project sandbox on tab close, reload, or navigation away — but as
 * a DETACH (`?reason=pagehide`), not a hard cancel. A reload fires `pagehide`;
 * an in-app route change does not, so the cleanup below covers it. The detach
 * keeps the session resident + driving for the server's reconnect grace
 * (`LOOM_SESSION_DETACH_GRACE_MS`) so the reloaded tab re-attaches (Layer-1
 * resume) instead of cold-booting a fresh pipeline. A genuine close that never
 * re-attaches is reaped by the server after the grace. Explicit "discard from
 * sidebar" and `/reset` stay HARD (a bare DELETE) at their own call sites.
 *
 * `fetch` + `keepalive` (not `sendBeacon`, which is POST-only) because the route
 * is DELETE-only and must outlive the page unload. No-op until a session exists.
 *
 * Used by every client surface that OWNS a project session (the wizard, the
 * refine flow, chat) so closing any of them detaches rather than cancels.
 *
 * In the Focus panel (an MCP App) the host's teardown is that unload: the
 * panel re-dispatches it as `PANEL_TEARDOWN_EVENT` (`lib/host.ts`), and the
 * same detach runs on it. The web never fires it.
 */
export const useSessionPagehideBeacon = (
  sessionId: string | null,
  opts: { readonly detachOnUnmount?: boolean } = {},
): void => {
  // A REF, deliberately not a dependency. The flag flips under a live mount
  // (the chat slot is armed only while the chat stage is on screen), and a flag
  // in the dep array would re-run the effect on that flip — firing the previous
  // run's cleanup, i.e. detaching a session the user is still holding, on a
  // mere stage switch. That is the same over-eager reap this opt-in exists to
  // prevent, one level down. Keying only on `sessionId` means the cleanup runs
  // when the session actually goes away, and reads the CURRENT intent then.
  const detachOnUnmountRef = useRef(opts.detachOnUnmount === true);
  detachOnUnmountRef.current = opts.detachOnUnmount === true;
  useEffect(() => {
    if (sessionId === null) return undefined;
    const beacon = (): void => detachSession(sessionId);
    window.addEventListener("pagehide", beacon);
    window.addEventListener(PANEL_TEARDOWN_EVENT, beacon);
    return () => {
      window.removeEventListener("pagehide", beacon);
      window.removeEventListener(PANEL_TEARDOWN_EVENT, beacon);
      if (!detachOnUnmountRef.current) return;
      // AND on unmount / rebind, but ONLY where the caller opts in. `pagehide`
      // covers tab close and reload; it does NOT fire on an in-app route
      // change, and the Focus routes are SIBLINGS with different component
      // functions — so leaving a chat with browser Back, or picking a rail row
      // while standing in a refine URL, unmounted the owner of a live session
      // and detached nothing. The sandbox stayed attached and billing until the
      // idle reaper took it ~15 minutes later.
      //
      // THE OPT-IN IS LOAD-BEARING, and the rule is: detach on unmount ONLY
      // where a re-attach can undo it. `markDetached` starts a 90s grace that
      // ONLY `markUsed` clears, and `markUsed` has exactly one caller —
      // `ws/stream.ts`, i.e. a WebSocket attach or its 25s heartbeat.
      // `POST /reconnect` re-arms the pipeline but does not attach, so a stage
      // that owns a session and opens NO socket (the create wizard's steps, the
      // refine issue picker) has no way to clear the mark. Arming it there
      // turned the grace into an unconditional countdown: measured live, a
      // pre-warmed sandbox was swept ~150s after an in-app navigation while the
      // tab sat open on the picker, and the next click paid a full cold boot —
      // four pipelines for one session in fifteen minutes.
      //
      // So the surfaces that hold a socket (chat, refine, the standalone chat
      // route) opt in; the pre-warm stages keep the pagehide-only behaviour,
      // where the unload is real and no return is coming.
      detachSession(sessionId);
    };
  }, [sessionId]);
};

/**
 * The detach above, as a one-shot call for the surfaces that leave a session
 * behind WITHOUT the page unloading: a history pick stepping off the session it
 * was on, and a mint whose binding moved out from under it while `POST
 * /sessions` was in flight. Same route, same `?reason=pagehide` semantics — the
 * sandbox enters the server's grace instead of being orphaned with its own
 * billed budget window.
 *
 * Fire-and-forget in both directions: the rejection is swallowed (an unhandled
 * rejection here would surface as a page-level error for something the server
 * reaps anyway) and nothing waits on it.
 */
export const detachSession = (sessionId: string): void => {
  try {
    void fetch(`/sessions/${sessionId}?reason=pagehide`, {
      method: "DELETE",
      keepalive: true,
    }).catch(() => {
      /* best-effort — the server reaps an un-detached session anyway */
    });
  } catch {
    /* best-effort */
  }
};

/**
 * The in-flight `POST /reconnect` per sessionId, resolving to whether the
 * bridge was actually re-armed. Never rejects — the outcome the joiners branch
 * on is the boolean, so a transport throw and a 502 are the same answer.
 *
 * Module scope, so two components mounting in the same tick share one entry.
 * The entry is dropped the moment the request settles — this is a dedupe, not a
 * cooldown and not a latch: a timer here would suppress a legitimate re-arm
 * after a real sandbox death.
 */
const ensureInFlight = new Map<string, Promise<boolean>>();

/**
 * One re-arm attempt. `true` only on an OK response: a non-OK re-arms nothing,
 * so the surface sits at "starting" with no pipeline — the very symptom this
 * hook exists to prevent, but silent. Deliberately no retry of its own (the
 * dispatch routes are rate-limited and a retry storm is worse than a stuck
 * tab); the point is that the failure is greppable and that it does not pass
 * for success.
 */
const requestReconnect = (sessionId: string): Promise<boolean> =>
  fetch(`/sessions/${sessionId}/reconnect`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: reconnectBody(),
  }).then(
    (res) => {
      if (!res.ok) {
        wsDiag("bridge_ensure_failed", { sessionId, status: res.status });
        return false;
      }
      return true;
    },
    (err: unknown) => {
      wsDiag("bridge_ensure_error", {
        sessionId,
        message: err instanceof Error ? err.message : String(err),
      });
      return false;
    },
  );

/**
 * Guarantee a bridge pipeline is running for a session the surface OWNS.
 *
 * `POST /sessions` is the only path that triggers a pipeline, and the per-tab
 * `useProjectSession` binding deliberately short-circuits it: a reload or
 * deep-link ADOPTS the persisted sessionId instead of cold-booting. That is
 * correct while the sandbox is alive, but once it is gone (detach grace
 * elapsed, pipeline canceled, bridge evicted) the adopted session attaches a
 * healthy WS over a sandbox that no longer exists — the surface sits at
 * "starting" forever with no pipeline behind it, and nothing re-triggers.
 *
 * `POST /reconnect` closes that gap. The server's early-return is guarded on
 * `bridgeTriggered`, claimed several awaits into the handler, so two calls that
 * cross in flight both get past it — the money boundaries are the per-session
 * reconnect mutex and the dispatcher's single-flight, both server-side.
 *
 * The dedupe removes the common pair at SOURCE: two surfaces own the same
 * project session and both mount in one gesture (the wizard plus the refine
 * flow, or chat), so one adopt-then-navigate fires two identical POSTs in the
 * same millisecond.
 *
 * A joiner settles on SUCCESS ONLY, and success is an OK RESPONSE rather than
 * a settled fetch. Inheriting the leader's failure would disarm the joiner with
 * one it never made — sitting at "starting" with no pipeline, the exact symptom
 * this hook exists to prevent — and a 502 from the dispatcher disarms exactly
 * as hard as a dropped connection, so both send every joiner off with its own
 * request. That retry can cost no extra pipeline: the two server-side
 * boundaries bound it.
 *
 * Belongs on EVERY surface that owns a project session — the pairing with
 * `useSessionPagehideBeacon` is the contract: the beacon detaches on unload,
 * this re-arms on open.
 */
export const useSessionBridgeEnsure = (sessionId: string | null): void => {
  useEffect(() => {
    if (sessionId === null) return undefined;
    let cancelled = false;
    // The Focus panel boots nothing before a gesture in it (`lib/host.ts`).
    // Its re-arm waits for that gesture, and a surface gone by then (a Back
    // click was the gesture) re-arms nothing. On the web `whenBootAllowed`
    // is null and the re-arm goes out at once, as it always has.
    const gate = whenBootAllowed();
    if (gate !== null) {
      void gate.then(() => {
        if (!cancelled) ensureBridge(sessionId, () => cancelled);
      });
      return () => {
        cancelled = true;
      };
    }
    ensureBridge(sessionId, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);
};

/** The re-arm itself: lead a `POST /reconnect`, or join the one in flight. */
const ensureBridge = (sessionId: string, isCancelled: () => boolean): void => {
  const leader = ensureInFlight.get(sessionId);
  if (leader !== undefined) {
    wsDiag("bridge_ensure_joined", { sessionId });
    void leader.then((armed) => {
      if (armed || isCancelled()) return undefined;
      return requestReconnect(sessionId);
    });
    return;
  }
  const attempt = requestReconnect(sessionId);
  ensureInFlight.set(sessionId, attempt);
  void attempt.finally(() => {
    if (ensureInFlight.get(sessionId) === attempt) {
      ensureInFlight.delete(sessionId);
    }
  });
};
